import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, ErrorBanner, Spinner } from '../components/ui.js';
import { api } from '../lib/api.js';
import { useAction } from '../lib/hooks.js';

interface Redemption {
  organizationId: string;
  organizationName: string;
  alreadyMember: boolean;
}

/**
 * Redeeming a join code.
 *
 * Reached two ways, and both matter:
 *
 *  - `/join/ABCD1234` from a link a publisher pasted into a chat. One tap, and
 *    the code is already filled in.
 *  - `/join` typed or navigated to, where the learner has the code on a
 *    whiteboard or in their ear and types it.
 *
 * A code in the URL is **not** redeemed automatically. It is shown, and the
 * learner presses a button — because the page can be opened by anything that
 * previews links, and a chat client fetching a preview must not silently
 * subscribe somebody. It also means the learner sees whose class they are
 * joining before they join it.
 */
export function JoinPage(): ReactNode {
  const { code: codeFromUrl } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeFromUrl ?? '');
  const [result, setResult] = useState<Redemption | null>(null);
  const { run, pending, error } = useAction();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      const redemption = await api.post<Redemption>('/api/join-codes/redeem', { code });
      setResult(redemption);
    });
  }

  if (result) {
    return (
      <div className="stack" style={{ maxWidth: '32rem', margin: '2rem auto' }}>
        <Card title={result.alreadyMember ? 'Already subscribed' : 'Subscribed'}>
          <p>
            {result.alreadyMember ? (
              <>
                You were already subscribed to <strong>{result.organizationName}</strong>.
              </>
            ) : (
              <>
                You are now subscribed to <strong>{result.organizationName}</strong>.
              </>
            )}
          </p>
          <p className="small muted">
            Their scheduled classes appear under Classes. You will not be charged — a
            subscription is how a teacher shares their classes with you.
          </p>
          <div className="button-row" style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              className="button-primary"
              onClick={() => void navigate(`/orgs/${result.organizationId}/live-sessions`)}
            >
              See their classes
            </button>
            <Link to="/" className="button-secondary">
              Home
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="stack" style={{ maxWidth: '32rem', margin: '2rem auto' }}>
      <Card title="Join a teacher’s classes">
        <ErrorBanner message={error} />

        <p className="field-hint">
          Enter the code your teacher gave you. Capitals, spaces and dashes make no difference.
        </p>

        <form onSubmit={(e) => void submit(e)} className="stack">
          <div>
            <label htmlFor="join-code">Join code</label>
            <input
              id="join-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="ABCD1234"
              /*
               * Uppercase and wide-spaced so a mistyped character is visible
               * before submitting. The server folds case anyway; this is for the
               * person, not the parser.
               */
              style={{
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: '1.15rem',
              }}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
            />
          </div>

          <div className="button-row">
            <button type="submit" className="button-primary" disabled={pending || !code.trim()}>
              {pending && <Spinner />}
              Subscribe
            </button>
          </div>
        </form>

        <p className="small faint" style={{ marginTop: '0.75rem' }}>
          If the code does not work, check it with whoever gave it to you — codes can be turned off
          or reach their limit.
        </p>
      </Card>
    </div>
  );
}
